import React, { useState, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';

export type DebouncedInputProps = {
  label: string;
  name: string;
  value: string;
  onDebouncedChange: (value: string) => void;
  type?: 'text' | 'number';
  min?: number;
  max?: number;
  debounceTimeMs?: number;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  labelBackgroundClassName?: string;
  showFloatingLabel?: boolean;
  placeholder?: string;
};

export const DebouncedInput = ({
  label,
  name,
  value,
  onDebouncedChange,
  type = 'text',
  min,
  max,
  debounceTimeMs = 300,
  containerClassName,
  labelClassName,
  inputClassName,
  labelBackgroundClassName,
  showFloatingLabel = true,
  placeholder,
  ...restProps
}: DebouncedInputProps): React.JSX.Element => {
  const [localValue, setLocalValue] = useState<string>(value);
  const inputSubjectRef = useRef<Subject<string> | null>(null);

  useEffect(() => {
    inputSubjectRef.current = new Subject<string>();
    return () => {
      inputSubjectRef.current?.complete();
    };
  }, []);

  useEffect(() => {
    if (!inputSubjectRef.current) return;
    const subscription = inputSubjectRef.current
      .pipe(debounceTime(debounceTimeMs))
      .subscribe((debouncedValue: string) => {
        onDebouncedChange(debouncedValue);
      });
    return () => {
      subscription.unsubscribe();
    };
  }, [debounceTimeMs, onDebouncedChange]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newVal = e.target.value;

    if (type === 'number' && newVal !== '' && isNaN(Number(newVal))) {
      return;
    }

    setLocalValue(newVal);
    if (inputSubjectRef.current) {
      inputSubjectRef.current.next(newVal);
    }
  };

  return (
    <InputAnimatedLabel
      label={label}
      name={name}
      value={localValue}
      onChange={handleChange}
      type={type}
      min={min}
      max={max}
      containerClassName={containerClassName}
      labelClassName={labelClassName}
      inputClassName={inputClassName}
      labelBackgroundClassName={labelBackgroundClassName}
      showFloatingLabel={showFloatingLabel}
      placeholder={placeholder ?? (showFloatingLabel ? '' : label)}
      {...restProps}
    />
  );
};
